import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {VerifiedIcon} from '@primer/octicons-react'
import {Heading, RelativeTime, Link} from '@primer/react'
import deploymentStyles from './DeploymentRecordList.module.css'

import type {RegistryAttestation} from '../types'

interface RegistryAttestationsListProps {
  attestations: RegistryAttestation[]
  org: string
}

export function RegistryAttestationsList({attestations, org}: RegistryAttestationsListProps) {
  const formatNumber = (num: number): string => {
    return num.toLocaleString()
  }
  const listTitle = `${formatNumber(attestations.length)} ${attestations.length === 1 ? 'item' : 'items'}`

  return (
    <div className="mt-4">
      <Heading as="h3" className={deploymentStyles.Header}>
        Attestations
      </Heading>

      <ListView
        variant="default"
        title="Attestations List"
        className="border rounded"
        metadata={<ListViewMetadata title={listTitle} />}
      >
        {attestations.map(attestation => (
          <RegistryAttestationListItem key={attestation.id} attestation={attestation} org={org} />
        ))}
      </ListView>
    </div>
  )
}
interface RegistryAttestationListItemProps {
  attestation: RegistryAttestation
  org: string
}

function RegistryAttestationListItem({attestation, org}: RegistryAttestationListItemProps) {
  const {id, runInvocationUri, workflowFile, repository} = attestation
  const interpunct = ' · '

  const attestationLink = `/${org}/${repository}/attestations/${id}`

  return (
    <ListItem
      title={
        <ListItemTitle href={attestationLink} anchorClassName="fgColor-default" value={attestation.predicateType} />
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>
          <VerifiedIcon size={16} className="mr-2 fgColor-success" verticalAlign="middle" />
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription className="fgColor-muted">
          <ListItemDescriptionItem>{`#${attestation.id}`}</ListItemDescriptionItem>
          <ListItemDescriptionItem>
            {interpunct}
            <RelativeTime
              data-testid="registry-attestation-list-item-date"
              datetime={attestation.createdAt}
              prefix=""
            />
            {' by'}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className="text-semibold">
            <Link className="fgColor-muted" href={runInvocationUri} data-testid="registry-attestation-workflow-link">
              {workflowFile}
            </Link>
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}
